import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCatalog } from '../app/providers/CatalogProvider.jsx';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { useAccess } from '../app/providers/AccessProvider.jsx';
import { loadMembershipPlans } from '../services/account/accountService.js';
import {
  activateMembership,
  createTvodOrder,
  loadPayPalSdk,
  persistTvodEntitlement,
} from '../services/commerce/commerceService.js';
import '../styles/commerce.css';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MEMBERSHIP_CHECKOUT_LOCK_TTL_MS = 30 * 60 * 1000;

function membershipCheckoutLockKey(userId, planKey) {
  return `watchvim_membership_checkout_lock:${String(userId || '')}:${String(planKey || '')}`;
}

function readMembershipCheckoutLock(key) {
  if (!key) return null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!parsed || typeof parsed !== 'object') return null;
    const createdAt = Number(parsed.createdAt || 0);
    if (!createdAt || Date.now() - createdAt > MEMBERSHIP_CHECKOUT_LOCK_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeMembershipCheckoutLock(key, value) {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify({ ...value, createdAt: Number(value?.createdAt || Date.now()) }));
  } catch (_) {}
}

function clearMembershipCheckoutLock(key) {
  if (!key) return;
  try { sessionStorage.removeItem(key); } catch (_) {}
}

function CheckoutShell({ title, subtitle, children }) {
  return (
    <section className="commerce-page">
      <div className="commerce-card">
        <span className="eyebrow">Secure WatchVIM Checkout</span>
        <h1>{title}</h1>
        {subtitle ? <p className="commerce-muted">{subtitle}</p> : null}
        {children}
      </div>
    </section>
  );
}

async function waitForMembership(access) {
  for (const delay of [0, 900, 1800, 3200]) {
    if (delay) await wait(delay);
    const next = await access.refresh();
    if (next?.subscription?.active) return next.subscription;
  }
  return null;
}

export function MembershipCheckoutPage() {
  const { ready, session, user } = useSession();
  const access = useAccess();
  const [params] = useSearchParams();
  const buttonHost = useRef(null);
  const buttonsRef = useRef(null);
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState('Loading membership checkout…');
  const [error, setError] = useState('');
  const [completed, setCompleted] = useState(false);
  const planKey = params.get('plan') === 'svod-annual' ? 'svod-annual' : 'svod-monthly';

  const selected = useMemo(() => {
    const id = planKey === 'svod-annual' ? 'annual' : 'monthly';
    return plans.find((plan) => String(plan.id).toLowerCase() === id) || null;
  }, [plans, planKey]);

  useEffect(() => {
    loadMembershipPlans()
      .then((rows) => setPlans(rows.filter((plan) => ['monthly', 'annual'].includes(String(plan.id).toLowerCase()))))
      .catch((err) => setError(err?.message || 'Unable to load membership pricing.'));
  }, []);

  useEffect(() => {
    if (completed || !user || !selected?.paypal_plan_id || !buttonHost.current) return undefined;
    const checkoutLockKey = membershipCheckoutLockKey(user.id, planKey);

    // Phase 4 duplicate-billing guard: an already-active member never receives
    // another PayPal subscription button for the same WatchVIM account.
    if (access.subscription.active) {
      setError('');
      setStatus('Your WatchVIM membership is already active. No additional subscription is needed.');
      try { buttonsRef.current?.close?.(); } catch (_) {}
      if (buttonHost.current) buttonHost.current.innerHTML = '';
      return undefined;
    }

    const existingLock = readMembershipCheckoutLock(checkoutLockKey);
    if (existingLock) {
      setError('');
      setStatus(existingLock.subscriptionId
        ? 'Your PayPal subscription is already being verified. Do not submit another payment.'
        : 'A PayPal membership checkout is already in progress. Complete or cancel that checkout before trying again.');
      try { buttonsRef.current?.close?.(); } catch (_) {}
      if (buttonHost.current) buttonHost.current.innerHTML = '';
      return undefined;
    }

    let active = true;
    setError('');
    setStatus('Loading PayPal…');

    loadPayPalSdk({ intent: 'subscription' })
      .then((paypal) => {
        if (!active || !buttonHost.current) return;
        try { buttonsRef.current?.close?.(); } catch (_) {}
        buttonHost.current.innerHTML = '';
        const buttons = paypal.Buttons({
          style: { shape: 'pill', label: 'subscribe', height: 48 },
          createSubscription: async (_data, actions) => {
            const activeLock = readMembershipCheckoutLock(checkoutLockKey);
            if (activeLock) {
              throw new Error(activeLock.subscriptionId
                ? 'This membership payment is already being verified. Do not submit another payment.'
                : 'A membership checkout is already in progress.');
            }

            writeMembershipCheckoutLock(checkoutLockKey, {
              state: 'creating',
              userId: String(user.id),
              planKey,
              paypalPlanId: selected.paypal_plan_id,
            });
            setStatus('Opening PayPal…');

            try {
              const subscriptionId = await actions.subscription.create({
                plan_id: selected.paypal_plan_id,
                custom_id: String(user.id),
                application_context: { brand_name: 'WatchVIM', user_action: 'SUBSCRIBE_NOW', shipping_preference: 'NO_SHIPPING' },
              });
              writeMembershipCheckoutLock(checkoutLockKey, {
                state: 'created',
                userId: String(user.id),
                planKey,
                paypalPlanId: selected.paypal_plan_id,
                subscriptionId: String(subscriptionId || ''),
              });
              return subscriptionId;
            } catch (err) {
              clearMembershipCheckoutLock(checkoutLockKey);
              throw err;
            }
          },
          onApprove: async (data) => {
            const subscriptionId = String(data?.subscriptionID || '').trim();
            if (!subscriptionId) {
              clearMembershipCheckoutLock(checkoutLockKey);
              throw new Error('PayPal did not return a subscription ID.');
            }
            writeMembershipCheckoutLock(checkoutLockKey, {
              state: 'verifying',
              userId: String(user.id),
              planKey,
              paypalPlanId: selected.paypal_plan_id,
              subscriptionId,
            });
            setStatus('PayPal approved. Verifying your WatchVIM membership…');
            try {
              const activation = await activateMembership({
                session,
                subscriptionId,
                plan: planKey,
                paypalPlanId: selected.paypal_plan_id,
                previousSubscriptionId: access.subscription.paypalSubscriptionId || '',
              });
              const activeMembership = activation?.svod_active === true || activation?.has_access === true || activation?.access_granted === true
                ? (await access.refresh())?.subscription
                : await waitForMembership(access);
              if (activeMembership?.active) {
                clearMembershipCheckoutLock(checkoutLockKey);
                // Do not redirect away before the viewer sees a definitive server-confirmed
                // completion state. Also remove the PayPal controls so a Fold/mobile viewer
                // cannot accidentally submit the same purchase again.
                setError('');
                setStatus('');
                setCompleted(true);
                return;
              }
              setStatus('PayPal approved your subscription. WatchVIM is still confirming activation. Do not submit another payment.');
            } catch (err) {
              setError(err?.message || 'Membership verification is still pending. Do not submit another payment.');
              setStatus('');
              // Keep the lock when PayPal already returned a subscription ID. The viewer
              // must not create another billing agreement while reconciliation is pending.
            }
          },
          onCancel: () => {
            clearMembershipCheckoutLock(checkoutLockKey);
            setStatus('Checkout cancelled. No WatchVIM access was changed.');
          },
          onError: (err) => {
            const currentLock = readMembershipCheckoutLock(checkoutLockKey);
            if (!currentLock?.subscriptionId) clearMembershipCheckoutLock(checkoutLockKey);
            setError(err?.message || 'PayPal checkout could not be completed.');
            setStatus('');
          },
        });
        buttonsRef.current = buttons;
        setStatus('');
        return buttons.render(buttonHost.current);
      })
      .catch((err) => { if (active) { setError(err?.message || 'Unable to load PayPal.'); setStatus(''); } });

    return () => {
      active = false;
      try { buttonsRef.current?.close?.(); } catch (_) {}
    };
  }, [user?.id, session?.access_token, selected?.paypal_plan_id, planKey, completed, access.subscription.active, access.subscription.paypalSubscriptionId]);

  if (ready && !user) return <Navigate to={`/login?return=${encodeURIComponent(`/checkout/membership?plan=${planKey}`)}`} replace />;
  const amount = selected ? Number(selected.price_usd ?? selected.price ?? 0).toFixed(2) : '';

  return (
    <CheckoutShell title={completed ? 'Welcome to WatchVIM' : (selected?.name || 'WatchVIM Membership')} subtitle={completed ? 'Your membership is active and confirmed.' : (selected ? `$${amount} ${selected.currency || 'USD'} · ${String(selected.interval || '').toLowerCase()}` : 'Loading current WatchVIM plan…')}>
      {completed ? (
        <div className="commerce-notice commerce-membership-success" role="status" aria-live="polite">
          <strong>Membership active</strong>
          <p>Your WatchVIM membership has been verified server-side and is connected to {user?.email || 'your signed-in account'}.</p>
          <p>A WatchVIM welcome confirmation will be sent to your account email. You can use the same WatchVIM login on supported devices, including Android.</p>
          <div className="commerce-links">
            <NavLink to="/home">Start Watching</NavLink>
            <NavLink to="/profile/membership">View Membership</NavLink>
          </div>
        </div>
      ) : (
        <>
          {access.subscription.active ? <div className="commerce-notice"><strong>Current membership:</strong> {access.subscription.plan || 'Active WatchVIM membership'} — another subscription cannot be started while this membership is active.</div> : null}
          {error ? <p className="commerce-message error" role="alert">{error}</p> : null}
          {status ? <p className="commerce-message" role="status">{status}</p> : null}
          <div ref={buttonHost} className="paypal-button-host" aria-label="PayPal membership checkout" />
          <div className="commerce-links"><NavLink to="/membership">← Back to membership plans</NavLink></div>
          <p className="commerce-footnote">Membership access is granted only after WatchVIM verifies the PayPal subscription server-side. A payment already being verified cannot be submitted again.</p>
        </>
      )}
    </CheckoutShell>
  );
}

export function TvodCheckoutPage() {
  const { titleId } = useParams();
  const { ready, session, user } = useSession();
  const access = useAccess();
  const { catalog, status: catalogStatus } = useCatalog();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const buttonHost = useRef(null);
  const buttonsRef = useRef(null);
  const quoteRef = useRef(null);
  const [status, setStatus] = useState('Preparing rental checkout…');
  const [error, setError] = useState('');
  const rentalScope = ['episode', 'season'].includes(params.get('rentalScope')) ? params.get('rentalScope') : 'title';
  const episodeId = params.get('episodeId') || '';
  const seasonNumber = params.get('seasonNumber') || '';
  const title = catalog?.titles?.find((item) => item.id === titleId || item.slug === titleId) || null;
  const episode = rentalScope === 'episode' ? title?.episodes?.find((item) => item.id === episodeId) || null : null;
  const displayTitle = episode?.title || title?.title || 'WatchVIM Rental';

  useEffect(() => {
    if (!user || !title || !buttonHost.current) return undefined;
    let active = true;
    setError('');
    setStatus('Loading secure PayPal checkout…');

    loadPayPalSdk({ intent: 'capture' })
      .then((paypal) => {
        if (!active || !buttonHost.current) return;
        try { buttonsRef.current?.close?.(); } catch (_) {}
        buttonHost.current.innerHTML = '';
        const buttons = paypal.Buttons({
          style: { shape: 'pill', label: 'paypal', height: 48 },
          createOrder: async () => {
            setStatus('Confirming the published WatchVIM rental price…');
            const quote = await createTvodOrder({ session, catalogTitleId: title.id, rentalScope, episodeId, seasonNumber: episode?.season || seasonNumber });
            quoteRef.current = quote;
            setStatus(`PayPal checkout · $${quote.amount} ${quote.currency}`);
            return quote.order_id;
          },
          onApprove: async (_data, actions) => {
            setStatus('Capturing payment…');
            const details = await actions.order.capture();
            const quote = quoteRef.current;
            const orderId = String(details?.id || quote?.order_id || '').trim();
            if (!quote || !orderId) throw new Error('PayPal payment returned without the WatchVIM order reference.');
            setStatus('Payment captured. Saving your WatchVIM access…');
            try {
              await persistTvodEntitlement({
                session,
                orderId,
                titleId: quote.entitlement_id,
                title: quote.title,
                rentalWindowHours: quote.rental_window_hours,
                rentalScope: quote.rental_scope,
                parentTitleId: quote.rental_scope === 'title' ? '' : quote.catalog_title_id,
                seasonNumber: quote.season_number || '',
                episodeId: quote.episode_id || '',
                amount: quote.amount,
                currency: quote.currency,
              });
              await access.refresh();
              setStatus('Rental active. Redirecting to playback…');
              if (quote.rental_scope === 'episode' && episode) navigate(`/watch/${encodeURIComponent(title.id)}/season/${episode.season}/episode/${episode.episode}`, { replace: true });
              else navigate(`/watch/${encodeURIComponent(title.id)}`, { replace: true });
            } catch (err) {
              setError(err?.message || 'Payment was captured but WatchVIM is still linking the entitlement. Do not pay again.');
              setStatus('');
            }
          },
          onCancel: () => setStatus('Rental checkout cancelled. No payment was captured.'),
          onError: (err) => { setError(err?.message || 'PayPal rental checkout could not be completed.'); setStatus(''); },
        });
        buttonsRef.current = buttons;
        setStatus('');
        return buttons.render(buttonHost.current);
      })
      .catch((err) => { if (active) { setError(err?.message || 'Unable to load PayPal rental checkout.'); setStatus(''); } });

    return () => {
      active = false;
      try { buttonsRef.current?.close?.(); } catch (_) {}
    };
  }, [user?.id, session?.access_token, title?.id, rentalScope, episode?.id, episode?.season, seasonNumber]);

  if (ready && !user) {
    const returnPath = `/checkout/${encodeURIComponent(titleId)}${window.location.search || ''}`;
    return <Navigate to={`/login?return=${encodeURIComponent(returnPath)}`} replace />;
  }
  if (catalogStatus === 'ready' && !title) return <Navigate to="/movies" replace />;

  return (
    <CheckoutShell title={`Rent ${displayTitle}`} subtitle="PayPal payment is verified against the server-published WatchVIM rental price before access is granted.">
      {error ? <p className="commerce-message error" role="alert">{error}</p> : null}
      {status ? <p className="commerce-message" role="status">{status}</p> : null}
      <div ref={buttonHost} className="paypal-button-host" aria-label="PayPal rental checkout" />
      <div className="commerce-links"><NavLink to={episode ? `/episode/${encodeURIComponent(title.id)}/${episode.season}/${episode.episode}` : `/title/${encodeURIComponent(title?.id || titleId)}`}>← Back to {displayTitle}</NavLink></div>
      <p className="commerce-footnote">If PayPal captures a payment but entitlement confirmation is interrupted, do not pay again. WatchVIM retains the PayPal order reference for reconciliation.</p>
    </CheckoutShell>
  );
}